import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, Link2, FileText, X, Loader2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";

export const MAX_DOCS = 5;
const btn = "font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50";

export const combineDossier = (docs) =>
  docs.map((d, i) => `=== Document ${i + 1} : ${d.label} ===\n${d.text.trim()}`).join("\n\n");

// "Dossier" tab: gather up to 5 sources (files, URLs, pasted texts) into one synthesis.
export function DossierPanel({ docs, setDocs, disabled }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [paste, setPaste] = useState("");
  const fileRef = useRef(null);
  const full = docs.length >= MAX_DOCS;
  const add = (text, label) => setDocs((d) => (d.length >= MAX_DOCS ? d : [...d, { id: crypto.randomUUID(), text, label }]));
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(err?.response?.data?.detail || t("dossier.read_error", "Impossible de lire cette source.")); } finally { setBusy(false); }
  };
  const onFiles = (e) => run(async () => {
    const files = Array.from(e.target.files || []).slice(0, MAX_DOCS - docs.length);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("language", i18n.resolvedLanguage || "auto");
      const { data } = await api.post("/extract/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      add(data.text, data.source_label);
    }
    if (fileRef.current) fileRef.current.value = "";
  });
  const onUrl = () => url.trim() && run(async () => {
    const { data } = await api.post("/extract/url", { url: url.trim() });
    add(data.text, data.source_label);
    setUrl("");
  });
  const onPaste = () => {
    if (paste.trim().length < 40) return toast.error(t("dossier.paste_short", "Collez au moins quelques phrases."));
    add(paste, `${t("dossier.text_label", "Texte")} : « ${paste.trim().slice(0, 40)}… »`);
    setPaste("");
  };
  const off = disabled || busy || full;
  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-5" data-testid="dossier-panel">
      <div className="flex items-start gap-3">
        <FolderOpen className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          {t("dossier.intro_1", "Rassemblez jusqu'à")} <b>{MAX_DOCS} {t("dossier.documents", "documents")}</b> {t("dossier.intro_2", "(fichiers, liens web, textes collés) : SUX1 en fait")} <b>{t("dossier.one_synthesis", "une seule synthèse")}</b>{" "}
          {t("dossier.intro_3", "qui croise les sources, avec les points communs, les différences et l'origine de chaque idée (Doc 1, Doc 2…). Crédits selon la longueur totale.")}
        </p>
      </div>
      <ol className="space-y-2" data-testid="dossier-list">
        {docs.map((d, i) => (
          <li key={d.id} className="flex items-center justify-between gap-3 border border-border px-4 py-3 bg-background" data-testid={`dossier-doc-${i}`}>
            <span className="text-sm min-w-0 truncate"><b className="text-[#002FA7] font-mono mr-2">Doc {i + 1}</b>{d.label}
              <span className="ml-2 text-[11px] text-muted-foreground">{d.text.length.toLocaleString(i18n.language)} {t("dossier.chars", "car.")}</span></span>
            <button type="button" onClick={() => setDocs((all) => all.filter((x) => x.id !== d.id))} disabled={disabled}
              aria-label={t("dossier.remove", "Retirer ce document")} data-testid={`dossier-remove-${i}`} className="text-muted-foreground hover:text-[#FF2A00]"><X className="w-4 h-4" /></button>
          </li>
        ))}
        {!docs.length && <li className="text-[12px] text-muted-foreground font-mono uppercase tracking-[0.14em]">{t("dossier.empty", "Aucun document pour l'instant")} · 0 / {MAX_DOCS}</li>}
      </ol>
      <div className="sux-marker flex items-center gap-1" data-testid="dossier-count">{docs.length} / {MAX_DOCS} {t("dossier.documents", "documents")}{docs.length === 1 ? ` · ${t("dossier.add_one_more", "ajoutez-en au moins un autre")}` : ""}
        <InfoTip testid="dossier-info" content={t("dossier.tip", "Ajoutez 2 à 5 sources, puis lancez le résumé comme d'habitude : format, niveau de classe et longueur s'appliquent à la synthèse. Le coût dépend de la longueur totale ; il est affiché avant de lancer. Retirez un document avec la croix.")} /></div>
      {busy && <div className="flex items-center gap-2 text-sm text-[#002FA7]"><Loader2 className="w-4 h-4 animate-spin" /> {t("dossier.reading", "Lecture du document…")}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.epub,.txt,.md,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={onFiles} data-testid="dossier-file-input" />
        <button type="button" className={btn} onClick={() => fileRef.current?.click()} disabled={off} data-testid="dossier-add-file-btn"><Upload className="w-3.5 h-3.5" /> {t("dossier.add_files", "Ajouter des fichiers")}</button>
        <InfoTip testid="dossier-files-info" content={t("dossier.files_tip", "PDF, Word (.docx), EPUB, texte ou photo (JPG, PNG, HEIC). Plusieurs fichiers à la fois possible. Pour un lien web, SUX1 récupère le texte de la page.")} />
      </div>
      <div className="flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" disabled={off} data-testid="dossier-url-input"
          className="flex-1 min-w-0 border border-border bg-background px-3 py-2 text-sm" />
        <button type="button" className={btn} onClick={onUrl} disabled={off || !url.trim()} data-testid="dossier-add-url-btn"><Link2 className="w-3.5 h-3.5" /> {t("dossier.add_url", "Ajouter le lien")}</button>
      </div>
      <div className="space-y-2">
        <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={4} placeholder={t("dossier.paste_placeholder", "Ou collez un texte ici…")} disabled={off} data-testid="dossier-paste-input"
          className="w-full border border-border bg-background px-3 py-2 text-sm" />
        <button type="button" className={btn} onClick={onPaste} disabled={off || !paste.trim()} data-testid="dossier-add-text-btn"><FileText className="w-3.5 h-3.5" /> {t("dossier.add_text", "Ajouter ce texte")}</button>
      </div>
    </div>
  );
}

export default DossierPanel;
